import { Column, Entity, Index, OneToMany } from 'typeorm';
import type { PromoDisplayLocation } from '../../constants/promo-display-locations';
import { BaseEntity } from './base.entity';
import { OrderPromoApplicationEntity } from './order-promo-application.entity';
import { PromoCategoryEntity } from './promo-category.entity';
import { PromoFileEntity } from './promo-file.entity';
import { PromoItemGroupEntity } from './promo-item-group.entity';
import { PromoItemEntity } from './promo-item.entity';
import { PromoShippingMethodEntity } from './promo-shipping-method.entity';
import { PromoTargetEntity } from './promo-target.entity';

export interface PromoImages {
	/**
	 * Primary 16:9 hero banner. Used by full-width banner widgets and
	 * the promo detail page hero. Also serves as the fallback when a
	 * widget asks for a more-specific aspect that the admin hasn't
	 * uploaded yet (CSS object-cover crops it).
	 */
	bannerUrl?: string;
	/** 4:3 variant for one-up tiles. Falls back to bannerUrl. */
	bannerUrl4x3?: string;
	/** 1:1 square variant for grid / two-up cells. Falls back to bannerUrl. */
	bannerUrlSquare?: string;
	/**
	 * Decorative no-text strip variant — themed background imagery only,
	 * no headline / subhead / badge baked in. Used by the section-header
	 * "strip" slot inside `promo-with-items` (and any future widget that
	 * wants a thin themed band rather than the full-banner display
	 * artwork). 3:1 native, but rendered into much thinner containers
	 * (8:1 mobile, 12:1 desktop) where the surrounding widget header
	 * already carries the promo title — so cropping to a wide thin band
	 * is intentional. Falls back to `bannerUrl` when the admin hasn't
	 * uploaded a strip variant yet.
	 */
	bannerStripUrl?: string;
	thumbnailUrl?: string;
	galleryUrls?: Array<string>;
}

// "Currently active" engine lookups filter on isActive + dates.
@Index('idx_promo_active_dates', [
	'isActive',
	'effectiveStartDate',
	'effectiveEndDate',
])
// Per-tenant uniqueness on coupon code (DB-per-tenant makes this implicit
// across tenants). Conditional on couponCode IS NOT NULL handled by the
// rename migration that creates this index because TypeORM @Index doesn't
// support partial indexes directly.
@Index('idx_promo_couponCode_unique', ['couponCode'], { unique: true })
@Entity('promo')
export class PromoEntity extends BaseEntity {
	@Column('varchar', { name: 'name', length: 255 })
	public name!: string;

	@Column('text', { name: 'description', nullable: true })
	public description!: string | null;

	@Column('varchar', { name: 'headline', nullable: true, length: 255 })
	public headline!: string | null;

	@Column('text', { name: 'subheadline', nullable: true })
	public subheadline!: string | null;

	@Column('varchar', { name: 'ctaText', nullable: true, length: 255 })
	public ctaText!: string | null;

	/**
	 * Click-through destination for the promo when surfaced on the
	 * storefront homepage / banner / carousel / etc. The renderer
	 * resolves these into actual URLs:
	 *   - `NONE`              → not clickable
	 *   - `CATEGORY`          → /catalog?category=<value>
	 *   - `ITEM`              → /product/<value>           (value = item id)
	 *   - `ITEM_GROUP`        → /catalog?itemGroup=<value> (value = group id)
	 *   - `EXTERNAL_URL`      → href=<value> (admin-supplied absolute url)
	 *
	 * Defaulting to NONE keeps existing promos behaving exactly as
	 * before (no click target). Admin opts in per promo when the
	 * promo is featured on the homepage.
	 */
	@Column('varchar', {
		name: 'ctaLinkType',
		length: 32,
		default: () => "'NONE'",
	})
	public ctaLinkType!:
		| 'NONE'
		| 'CATEGORY'
		| 'ITEM'
		| 'ITEM_GROUP'
		| 'EXTERNAL_URL';

	@Column('varchar', { name: 'ctaLinkValue', nullable: true, length: 1024 })
	public ctaLinkValue!: string | null;

	@Column('text', { name: 'termsAndConditions', nullable: true })
	public termsAndConditions!: string | null;

	@Column('varchar', { name: 'discountType', length: 32 })
	public discountType!: 'PERCENT' | 'DOLLARS' | 'FIXED_PRICE' | 'BUY_X_GET_Y';

	@Column('varchar', { name: 'appliesAt', length: 16, default: () => "'LINE'" })
	public appliesAt!: 'LINE' | 'ORDER' | 'SHIPPING';

	@Column('decimal', {
		name: 'discountValue',
		precision: 15,
		scale: 6,
		default: () => "'0.000000'",
	})
	public discountValue!: string;

	@Column('int', { name: 'buyQty', nullable: true })
	public buyQty!: number | null;

	@Column('int', { name: 'getQty', nullable: true })
	public getQty!: number | null;

	@Column('varchar', { name: 'getDiscountType', nullable: true, length: 16 })
	public getDiscountType!: 'PERCENT' | 'DOLLARS' | 'FREE' | null;

	@Column('decimal', {
		name: 'getDiscountValue',
		nullable: true,
		precision: 15,
		scale: 6,
	})
	public getDiscountValue!: string | null;

	@Column('varchar', {
		name: 'getAllocationPolicy',
		length: 16,
		default: () => "'HIGHEST_PRICE'",
	})
	public getAllocationPolicy!: 'HIGHEST_PRICE' | 'LOWEST_PRICE';

	// Cost impact — how the promo affects each affected line's cost
	// (acquisitionCost / sellCost), independent of the customer-facing
	// price discount above. Used for GP-impact reporting and
	// vendor-funded promo accounting:
	//   NONE        — cost is unchanged (default; merchant funds the
	//                 discount entirely).
	//   PERCENT     — line cost reduced by N% of original cost.
	//   DOLLARS     — per-unit cost reduced by N dollars (multiplied by
	//                 quantity at apply time).
	//   FIXED_PRICE — line cost is set to N (per-unit). Common for
	//                 vendor-funded BXGY where the GET item is shipped
	//                 to the merchant at $0 and we want GP to reflect
	//                 that.
	// Applies to whichever lines the discount applies to (BUY-or-GET
	// for BXGY → GET in practice; BUY for non-BXGY).
	@Column('varchar', {
		name: 'costImpactType',
		length: 16,
		default: () => "'NONE'",
	})
	public costImpactType!: 'NONE' | 'PERCENT' | 'DOLLARS' | 'FIXED_PRICE';

	@Column('decimal', {
		name: 'costImpactValue',
		nullable: true,
		precision: 15,
		scale: 4,
	})
	public costImpactValue!: string | null;

	@Column('tinyint', { name: 'isActive', width: 1, default: () => "'1'" })
	public isActive!: boolean;

	@Column('tinyint', { name: 'stackable', width: 1, default: () => "'0'" })
	public stackable!: boolean;

	@Column('int', { name: 'priority', default: () => "'100'" })
	public priority!: number;

	@Column('datetime', { name: 'effectiveStartDate' })
	public effectiveStartDate!: Date;

	@Column('datetime', { name: 'effectiveEndDate' })
	public effectiveEndDate!: Date;

	// Stored upper-cased; engine matches case-insensitively by upper-casing
	// the entered code before comparison. Per-tenant unique (DB-per-tenant).
	@Column('varchar', { name: 'couponCode', nullable: true, length: 64 })
	public couponCode!: string | null;

	@Column('decimal', {
		name: 'maxDiscountAmount',
		nullable: true,
		precision: 15,
		scale: 2,
	})
	public maxDiscountAmount!: string | null;

	@Column('int', { name: 'minLineQuantity', nullable: true })
	public minLineQuantity!: number | null;

	@Column('decimal', {
		name: 'minLineSubtotal',
		nullable: true,
		precision: 15,
		scale: 2,
	})
	public minLineSubtotal!: string | null;

	@Column('int', { name: 'minCartQuantity', nullable: true })
	public minCartQuantity!: number | null;

	@Column('decimal', {
		name: 'minCartSubtotal',
		nullable: true,
		precision: 15,
		scale: 2,
	})
	public minCartSubtotal!: string | null;

	@Column('tinyint', {
		name: 'isFirstOrderOnly',
		width: 1,
		default: () => "'0'",
	})
	public isFirstOrderOnly!: boolean;

	@Column('int', { name: 'maxUsesTotal', nullable: true })
	public maxUsesTotal!: number | null;

	@Column('int', { name: 'maxUsesPerCustomer', nullable: true })
	public maxUsesPerCustomer!: number | null;

	@Column('tinyint', {
		name: 'displayOnDashboard',
		width: 1,
		default: () => "'0'",
	})
	public displayOnDashboard!: boolean;

	@Column('tinyint', {
		name: 'displayOnItemDetail',
		width: 1,
		default: () => "'0'",
	})
	public displayOnItemDetail!: boolean;

	@Column('tinyint', {
		name: 'displayBadgeInLists',
		width: 1,
		default: () => "'1'",
	})
	public displayBadgeInLists!: boolean;

	// Promo images stored as a single JSON blob:
	//   { bannerUrl?, thumbnailUrl?, galleryUrls?: string[] }
	// Banner used in storefront hero carousel; thumbnail in promo cards;
	// gallery for additional shots. URLs come from PublicCdnUploadService
	// uploads (no auth wall — these are public-facing). PromoFileEntity
	// remains in the schema for future multi-image needs but is unused
	// in Phase 3.
	@Column('json', { name: 'imagesJson', nullable: true })
	public imagesJson!: PromoImages | null;

	/**
	 * Closed-enum tags identifying which storefront dashboard widgets
	 * this promo is eligible to surface in. Stored as a JSON array of
	 * strings; values are validated against `PROMO_DISPLAY_LOCATIONS`.
	 *
	 * Why JSON not `SET`:
	 *   - Easy to extend the enum without `ALTER TABLE` per tenant
	 *   - Tenants typically have <100 promos so we don't need an index
	 *     for filter performance — engine pre-filters to "active +
	 *     displayOnDashboard" before the location filter sees it
	 *   - Migrate to a `SET` column later if measurement says we need it
	 *
	 * Empty array `[]` (the default) means "untagged" — the promo only
	 * shows in widgets configured without a `displayLocations` filter
	 * (i.e. legacy `auto` mode that takes everything eligible).
	 */
	@Column('json', { name: 'displayLocations', nullable: true })
	public displayLocations!: Array<PromoDisplayLocation> | null;

	@OneToMany(() => PromoItemEntity, (promoItem) => promoItem.fkPromo, {})
	public promoItems!: Array<PromoItemEntity>;

	@OneToMany(
		() => PromoItemGroupEntity,
		(promoItemGroup) => promoItemGroup.fkPromo,
	)
	public promoItemGroups!: Array<PromoItemGroupEntity>;

	// Item-category assignments — keyed by the storefront-facing
	// `item.category` string (the same one the homepage Category Tiles
	// widget targets). Engine matching: cart line qualifies when
	// `line.item.category` matches any row here. See
	// PromoCategoryEntity for the rationale on string-keying.
	@OneToMany(
		() => PromoCategoryEntity,
		(promoCategory) => promoCategory.fkPromo,
	)
	public promoCategories!: Array<PromoCategoryEntity>;

	@OneToMany(() => PromoTargetEntity, (promoTarget) => promoTarget.fkPromo)
	public promoTargets!: Array<PromoTargetEntity>;

	@OneToMany(() => PromoFileEntity, (promoFile) => promoFile.fkPromo)
	public promoFiles!: Array<PromoFileEntity>;

	@OneToMany(
		() => PromoShippingMethodEntity,
		(promoShippingMethod) => promoShippingMethod.fkPromo,
	)
	public promoShippingMethods!: Array<PromoShippingMethodEntity>;

	@OneToMany(
		() => OrderPromoApplicationEntity,
		(application) => application.fkPromo,
	)
	public applications!: Array<OrderPromoApplicationEntity>;
}
